import { useCallback, useEffect, useRef, useState } from "react";
import { prefersReducedMotion } from "~/utils/motion";

/**
 * Keep an element mounted through its exit animation.
 *
 * The protocol has more traps than it looks (each was shipped and fixed
 * separately before this hook existed — in ChatbotModal's panel exit and
 * MessageList's reading indicator):
 *
 * - `mounted` seeds from `open` and drives the caller's null-return. Gating
 *   on a flag an effect sets instead paints one frame with the element gone,
 *   then remounts it — a blink, not an exit.
 * - `exiting` is DERIVED (`mounted && !open`), never parallel state: a stored
 *   copy survives an abort and leaks onto the next mount as a replayed exit.
 * - Reduced motion unmounts on the same tick — there is no animation to hold
 *   the element open for, and waiting a timer leaves it inert but opaque.
 * - The timer fires even in hidden tabs (clamped to ~1s), where CSS
 *   animations freeze at t=0 and `animationend` never comes. Callers that
 *   unmount on `animationend` should pass `exitMs` with slack and treat the
 *   timer as the fallback, calling `unmountNow()` from the event.
 * - `resumed` is true while the current appearance came from reopening
 *   mid-exit. Re-exposing an entrance animation at that moment restarts it as
 *   a NEW animation — a `both`/`backwards` fill snaps the half-faded element
 *   to the keyframes' start before replaying. Callers should suppress their
 *   entrance while `resumed` (it clears on the next real unmount).
 */
export function useAnimatedUnmount(open: boolean, exitMs: number) {
  const [mounted, setMounted] = useState(open);
  const [resumed, setResumed] = useState(false);
  const prevOpenRef = useRef(open);

  const exiting = mounted && !open;

  useEffect(() => {
    const wasOpen = prevOpenRef.current;
    prevOpenRef.current = open;
    if (open) {
      // Reopened while the exit was still playing: same node, keep it.
      if (!wasOpen && mounted) setResumed(true);
      setMounted(true);
      return;
    }
    if (!mounted) return;
    if (prefersReducedMotion()) {
      setMounted(false);
      setResumed(false);
      return;
    }
    const t = setTimeout(() => {
      setMounted(false);
      setResumed(false);
    }, exitMs);
    return () => clearTimeout(t);
  }, [open, mounted, exitMs]);

  /** Finish early — e.g. from the exit animation's own `animationend`. */
  const unmountNow = useCallback(() => {
    setMounted(false);
    setResumed(false);
  }, []);

  return { mounted, exiting, resumed, unmountNow };
}
